import { Dropdown, Tooltip } from "bootstrap";

import { Application } from "@hotwired/stimulus";
import controllers from "stimulus-controllers";

import posthog from "posthog-js";
import * as Sentry from "@sentry/browser";

import "./main.scss";
import { Chart } from "chart.js/auto";

export * as zxinglib from "@zxing/library";

import mapboxgl from "mapbox-gl";
console.log("Loading MapBoxGL", mapboxgl.version);
export { mapboxgl };

// Select2 does not allow to overwrite the placeholder text for the
// search input (inside the dropdown). The following code fixes this by
// copying the `data-search-placeholder` attribute from the select
// element to the placeholder of the search box which select2 generates.
// Additionally, the width of the input element is hard-coded to be one
// character wide (for whatever reason?). This code changes the width back
// to 100%.
// The code here is more complex because the dropdown of select2 are
// attached to the body of the DOM tree and not anywhere near the select
// element. The code is meant to be failsafe as it relies on internal
// implementation details of select2, namely the construction of the
// `aria-controls` property of the search input.
// jQuery code because select2 is a jquery plugin.
function hotpatchSelect2SearchInputPlaceholder(s2e) {
  const placeholder = $(s2e.currentTarget).data("search-placeholder");
  if (placeholder) {
    const s2id = $(s2e.currentTarget).attr("data-select2-id");
    if (s2id) {
      const searchInput = $(
        'input[aria-controls="select2-' + s2id + '-results"]',
      );
      if (searchInput) {
        searchInput.attr("placeholder", placeholder);
        searchInput.css("width", "100%");
      }
    }
  }
}

export function init() {
  document.addEventListener("turbo:load", (/*event*/) => {
    // On loading a new page, instantiate all tooltips
    document
      .querySelectorAll('[data-bs-toggle="tooltip"]')
      .forEach((tooltipTriggerEl) => new Tooltip(tooltipTriggerEl));

    // On loading a new page, instantiate all dropdowns
    document
      .querySelectorAll(".dropdown-toggle")
      .forEach((dropdownToggleEl) => new Dropdown(dropdownToggleEl));

    // On loading a new page, instatiate the select2 elements
    $(".django-select2").djangoSelect2();
    $(".django-select2").on(
      "select2:open",
      hotpatchSelect2SearchInputPlaceholder,
    );
  });

  document.addEventListener("turbo:frame-load", (e) => {
    // On loading a new frame, instantiate select2 dropdowns

    // select2 (and therefore django-select2) require that you do
    // something special if the dropdown is inside a modal. See
    // https://select2.org/dropdown#dropdown-placement
    // This code checks if the new frame was placed inside a (boostrap)
    // modal. If yes, the parent of the turbo frame (which is the body of
    // the modal) is provided to the instatiation method of the
    // django-select2 constructor.
    let dropdownParent = document.body;
    if (
      e &&
      e.srcElement &&
      e.srcElement.parentElement &&
      e.srcElement.parentElement.classList.contains("modal-body")
    ) {
      dropdownParent = e.srcElement.parentElement;
    }

    // Intatiate all django-select2 dropdowns that are inside this frame.
    $(".django-select2").djangoSelect2({
      dropdownParent: dropdownParent,
    });
    $(".django-select2").on(
      "select2:open",
      hotpatchSelect2SearchInputPlaceholder,
    );
  });

  document.addEventListener("turbo:before-cache", (/*event*/) => {
    // Before caching a website, remove all tooltips. This makes sure that
    // if the user navigates back, there are no open tooltips.
    document
      .querySelectorAll('[data-bs-toggle="tooltip"]')
      .forEach((tooltipTriggerEl) => {
        const tooltipInstance = Tooltip.getInstance(tooltipTriggerEl);
        if (tooltipInstance) {
          tooltipInstance.dispose();
        }
      });

    // Before caching a website, remove all dropdowns. Same reason as above
    document.querySelectorAll(".dropdown-toggle").forEach((dropdownEl) => {
      const dropdownInstance = Dropdown.getInstance(dropdownEl);
      if (dropdownInstance) {
        dropdownInstance.hide();
        dropdownInstance.dispose();
      }
    });
  });
}

const application = Application.start();
application.load(controllers);

export function initPosthog(posthogAPIKey) {
  posthog.init(posthogAPIKey, { api_host: "https://eu.posthog.com" });
  return posthog;
}

export function initSentry(sentryDSN, company) {
  Sentry.init({
    dsn: sentryDSN,
    environment: company,
    tracesSampleRate: 0,
  });
}

export const chartColor = {
  danger: {
    default: "RGBA(220,53,69,0.8)",
    hover: "RGBA(220,53,69,1)",
  },
  warning: {
    default: "RGBA(255,193,7,0.8)",
    hover: "RGBA(255,193,7,1)",
  },
  primary: {
    default: "RGBA(13,110,253,0.8)",
    hover: "RGBA(13,110,253,1)",
  },
  success: {
    default: "RGBA(25,135,84,0.8)",
    hover: "RGBA(25,135,84,1)",
  },
  secondary: {
    default: "RGBA(33,37,41,0.3)",
    hover: "RGBA(33,37,41,0.4)",
  },
};

export function buildChartFromConfig(config) {
  /**
   * Config Structure:
   * config = {
   *     elementId: string, // elementId of the Chart Canvas
   *     label?: string, // label to be added to the data hover
   *     info: Info[],
   * }
   * Info = {
   *     label: string, // label of the data
   *     color: chartColor,
   *     data: number // count of elements
   *     target?: string // url where to link to
   * }
   */
  const chartStatus = Chart.getChart(config.elementId);
  if (chartStatus != undefined) {
    // If the chart is already bound to the canvas, destroy it first.
    // This can happen with Turbo Navigations
    chartStatus.destroy();
  }

  new Chart(document.getElementById(config.elementId), {
    type: "doughnut",
    data: {
      labels: config.info.map((i) => i.label),
      datasets: [
        {
          label: config.label ?? " Anzahl Geräte",
          data: config.info.map((i) => i.data),
          borderWidth: 3,
          backgroundColor: config.info.map((i) => i.color.default),
          hoverBackgroundColor: config.info.map((i) => i.color.hover),
        },
      ],
    },
    options: {
      animation: true,
      onClick: (e) => {
        const points = e.chart.getElementsAtEventForMode(
          e,
          "nearest",
          {
            intersect: true,
          },
          true,
        );
        if (points && points.length == 1) {
          const target = config.info[points[0].index].target;
          if (target) {
            document.location = target;
          }
        }
      },
      onHover: (event, chartElement) => {
        const mainElement = chartElement[0];
        if (!mainElement) {
          event.native.target.style.cursor = "default";
          return;
        }
        const points = event.chart.getElementsAtEventForMode(
          event,
          "nearest",
          {
            intersect: true,
          },
          true,
        );
        if (
          points &&
          points.length == 1 &&
          config.info[points[0].index].target
        ) {
          event.native.target.style.cursor = "pointer";
          return;
        }
        event.native.target.style.cursor = "default";
      },
    },
  });
}
